import {
  ApolloClient,
  LazyQueryExecFunction,
  OnDataOptions,
  OperationVariables,
  SubscribeToMoreUpdateQueryFn,
  SubscriptionResult,
} from '@apollo/client'
import { ApolloParams } from '@data-grid/index'
import {
  Account,
  ChangeType,
  CommentEvent,
  CommentsReadEvent,
  EventType,
  GetThreadQuery,
  GetThreadQueryVariables,
  SizeMatrix,
  Status,
  Thread,
  ThreadCommentActivity,
  ThreadCommentsSubscription,
  ThreadGoalActivity,
  ThreadLikeActivity,
  ThreadLikeEvent,
  ThreadLikesSubscription,
  ThreadTaskActivity,
  Timestamp,
} from '@lib/graphql/__generated__/graphql'
import { cache } from '@lib/graphql/cache'
import {
  ACCOUNT_FRAGMENT,
  ACCOUNT_FULL_FRAGMENT,
} from '@lib/graphql/fragments/Organization'
import { COMMENT_FRAGMENT } from '@lib/graphql/fragments/Task'
import { GET_COUNTERS } from '@lib/graphql/queries/Shared'
import { SEARCH_RECENT_ACTIVITY_THREADS } from '@lib/graphql/queries/Threads'
import { Board } from '@lib/types/boards'
import { FashionCollection } from '@lib/types/collections'
import { Comment } from '@lib/types/comment'
import { UUID } from '@lib/types/general'
import { Goal } from '@lib/types/goals'
import { Project } from '@lib/types/project'
import { Task } from '@lib/types/task'
import { User } from '@lib/types/user'
import { dateComparator } from '@mono/packages/data-grid/utils/comparators'
import { debounce } from '@utils/common'
import { getNanos, getTimeStamp } from '@utils/Date'
import { getDynamicPlanningRowId } from '@utils/dynamicReminders'
import lensPath from 'ramda/src/lensPath'
import path from 'ramda/src/path'
import set from 'ramda/src/set'
import view from 'ramda/src/view'

const DEBOUNCE_TIME_MS = 800

const refreshGridOnSubscriptionData: ApolloParams['onSubscriptionData'] = ({
  result,
  gridApi,
}) => {
  if (!gridApi) return

  if (typeof result.data == 'object') {
    gridApi.refreshServerSide()
  }
}

export const dynamicRemindersSubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const data = subscriptionData?.data

  if (!data) return prev
  const cachedData = prev.dynamicPlannings ?? []
  const subscriptionResult = data.dynamicReminders

  if (subscriptionResult?.__typename === 'EntityDeletedEvent') {
    return {
      ...prev,
      dynamicPlannings: cachedData.filter(
        (planning) =>
          getDynamicPlanningRowId(planning) !== subscriptionResult?.entityId,
      ),
    }
  }

  const type = data.dynamicReminders?.type as EventType
  const dynamicPlanningData = data.dynamicReminders?.dynamicPlanning

  if (!dynamicPlanningData) return prev

  const eventTypeUpdaters = {
    [EventType.Created]: (cachedData, dynamicPlanningData) => {
      const existingData = cachedData.find(
        (planning) =>
          getDynamicPlanningRowId(planning) ===
          getDynamicPlanningRowId(dynamicPlanningData),
      )

      if (existingData) return cachedData

      return [...cachedData, dynamicPlanningData]
    },
    [EventType.Updated]: (cachedData, dynamicPlanningData) => {
      return cachedData.map((planning) =>
        getDynamicPlanningRowId(planning) ===
        getDynamicPlanningRowId(dynamicPlanningData)
          ? dynamicPlanningData
          : planning,
      )
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  return {
    ...prev,
    dynamicPlannings: eventTypeUpdater(cachedData, dynamicPlanningData),
  }
}

const tasksSubscriptionDataHandler = (
  lens: (data) => any,
  prev: Record<string, any>,
  { subscriptionData },
  newPosition: 'top' | 'bottom',
  updateSortAscending: boolean = true,
) => {
  const data = Object.values(subscriptionData?.data ?? {})?.[0] as
    | {
        __typename: 'TaskEvent'
        deletedStandbyTaskId?: UUID
        type: EventType
        task: Task
      }
    | {
        __typename: 'EntityDeletedEvent'
        entityId: UUID
      }

  if (!data) return prev

  if (data.__typename === 'EntityDeletedEvent') {
    const cachedData = view(lens, prev) ?? []
    const newData = cachedData.filter((task) => task.id !== data.entityId)
    return set(lens, newData, prev)
  }

  const type = data.type
  const taskData = data.task
  const deletedStandbyTaskId = data.deletedStandbyTaskId
  const cachedData = view(lens, prev) ?? []

  if (!taskData) return prev

  const eventTypeUpdaters = {
    [EventType.Created]: (
      cachedData: Task[],
      taskData: Task,
      deletedStandbyTaskId: UUID,
    ) => {
      let updatedData = cachedData

      if (deletedStandbyTaskId) {
        const taskIndex = cachedData.findIndex(
          (task) => task.id === deletedStandbyTaskId,
        )

        if (taskIndex > -1) {
          const dataCopy = [...cachedData]

          dataCopy.splice(taskIndex, 1, taskData)

          return dataCopy
        }
      }

      const existingData = updatedData.find((task) => task.id === taskData.id)

      if (existingData) return updatedData

      updatedData =
        newPosition === 'top'
          ? [taskData, ...updatedData]
          : [...updatedData, taskData]

      return updatedData
    },
    [EventType.Updated]: (cachedData: Task[], taskData: Task) => {
      let newData = cachedData.map((task) =>
        task.id === taskData.id ? taskData : task,
      )

      const isTaskInCachedData = cachedData.find(
        (cachedTask) => cachedTask.id === taskData.id,
      )

      if (!isTaskInCachedData) {
        newData = [...newData, taskData]
      }

      if (isTaskInCachedData && taskData.status === Status.Canceled) {
        newData = newData.filter((task) => task.id !== taskData.id)
      }

      newData.sort((a, b) =>
        dateComparator('deadline', updateSortAscending)(a, b),
      )

      return newData
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  const newData = eventTypeUpdater(cachedData, taskData, deletedStandbyTaskId)

  return set(lens, newData, prev)
}

export const relatedTasksSubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const taskTreeData: Task = subscriptionData?.data?.taskTree?.taskTree

  if (!Array.isArray(taskTreeData)) return prev

  return {
    ...prev,
    getRelatedTasks: taskTreeData,
  }
}

export const projectDetailsSubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const data = subscriptionData?.data

  if (!data) return prev

  const type = data.project?.type as EventType
  const projectData: Project = data.project?.project
  const cachedData = prev.getProject ?? {}

  if (!projectData) return prev

  const eventTypeUpdaters = {
    [EventType.Updated]: (cachedData: Project) => {
      return {
        ...cachedData,
        ...projectData,
      }
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  return {
    ...prev,
    getProject: eventTypeUpdater(cachedData),
  }
}

export const goalDetailsSubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const data = subscriptionData?.data

  if (!data) return prev

  const type = data.goal?.type as EventType
  const goalData: Goal = data.goal?.goal
  const cachedData = prev.getGoal ?? {}

  if (!goalData) return prev

  const eventTypeUpdaters = {
    [EventType.Updated]: (cachedData: Goal) => {
      return {
        ...cachedData,
        ...goalData,
      }
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  return {
    ...prev,
    getGoal: eventTypeUpdater(cachedData),
  }
}

export const recurringDefinitionSubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const data = subscriptionData?.data

  if (!data) return prev

  const type = data.project?.type as EventType
  const recurringDefinitionData = data.recurringDefinition?.definition
  const cachedData = prev.getRecurringDefinition ?? {}

  if (!recurringDefinitionData) return prev

  const eventTypeUpdaters = {
    [EventType.Updated]: (cachedData) => {
      return {
        ...cachedData,
        ...recurringDefinitionData,
      }
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  return {
    ...prev,
    getRecurringDefinition: eventTypeUpdater(cachedData),
  }
}

export const logEntrySubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const data = subscriptionData?.data
  const newActivityLog = data?.activityLog

  if (!newActivityLog) return prev

  const BUFFER_IN_SECONDS = 2
  // This handles duplicate followers logs that are received,
  // because when we have root task with single duplicate
  // we copy the followers, meaning we will receive 2 events here for the same action
  const similiarLogExists = prev.getLogEntries?.some(
    (log) =>
      (log.changeType === ChangeType.FollowerChanged &&
        Math.abs(log.createdAt.seconds - newActivityLog.createdAt.seconds) <=
          BUFFER_IN_SECONDS &&
        newActivityLog.entityId !== log.entityId &&
        log.createdBy.id === newActivityLog.createdBy.id) ||
      log.id === newActivityLog.id,
  )

  if (similiarLogExists) return prev

  return {
    ...prev,
    getLogEntries: [...(prev.getLogEntries ?? []), newActivityLog],
  }
}

const myGoalsSubscriptionDataHandler = (
  subscriptionName: string,
  lens,
  prev,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  if (!data) return prev

  if (data[subscriptionName]?.__typename === 'EntityDeletedEvent') {
    const cachedData = view(lens, prev) ?? []
    const entityId = data[subscriptionName]?.entityId
    const updatedData = cachedData.filter((g) => g.id !== entityId)
    return set(lens, updatedData, prev)
  }

  const type = data[subscriptionName]?.type as EventType
  const goalData: Goal = data[subscriptionName]?.goal

  const cachedData = view(lens, prev) ?? []

  if (!goalData) return prev

  const eventTypeUpdaters = {
    [EventType.Created]: (cachedData: Goal[]) => {
      if (cachedData.find((g) => g.id === goalData.id)) return cachedData

      return [...cachedData, goalData]
    },
    [EventType.Updated]: (cachedData: Goal[]) => {
      return cachedData.map((g) =>
        g.id === goalData.id ? { ...g, ...goalData } : g,
      )
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  const updatedData = eventTypeUpdater(cachedData)

  return set(lens, updatedData, prev)
}

export const boardsSubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const data = subscriptionData?.data

  if (!data) return prev

  const type = data.boards?.type as EventType
  const boardData: Board = data.boards?.board
  const cachedData = prev.searchBoards?.boards ?? []

  if (data.boards?.__typename === 'EntityDeletedEvent') {
    return {
      ...prev,
      searchBoards: {
        ...prev.searchBoards,
        boards: cachedData.filter((g) => g.id !== data.boards?.entityId),
      },
    }
  }

  if (!boardData) return prev

  const eventTypeUpdaters = {
    [EventType.Created]: (cachedData: Board[], boardData: Board) => {
      const existingData = cachedData.find((board) => board.id === boardData.id)

      if (existingData) return cachedData

      return [boardData, ...cachedData]
    },
    [EventType.Updated]: (cachedData: Board[], boardData: Board) => {
      return cachedData.map((board) =>
        board.id === boardData.id ? boardData : board,
      )
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  return {
    ...prev,
    searchBoards: {
      ...prev.searchBoards,
      boards: eventTypeUpdater(cachedData, boardData),
    },
  }
}

export const accountsSubscriptionDataHandler = (
  currentUserAccount: User,
  { client, data: subscriptionResult }: OnDataOptions<any>,
  onLogout: Function,
) => {
  const { data } = subscriptionResult

  const type = data.accounts?.type as EventType
  const account = data.accounts?.account as User

  if (!type || !account) return

  const cache = client.cache

  const eventTypeUpdaters = {
    [EventType.Updated]: () => {
      if (account.id === currentUserAccount?.id && account.isActive === false) {
        // current logged in user has been deactivated
        // log him out
        onLogout()
        return
      }

      cache.writeFragment({
        id: account.id,
        data: account,
        fragment: ACCOUNT_FRAGMENT,
        fragmentName: 'AccountFragment',
      })
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (eventTypeUpdater) eventTypeUpdater()
}

export const newNotificationSubscriptionDataHandler = ({
  client,
  data,
}: OnDataOptions<any>) => {
  client.cache.updateQuery(
    {
      query: GET_COUNTERS,
    },
    (queryData) => {
      if (!queryData) return queryData

      const lens = lensPath(['getCounters', 'notifications'])
      return set(lens, data.data?.notificationsCounter, queryData)
    },
  )
}

const commentsSubscriptionDataHandler = (
  lens: (data) => any,
  prev: Record<string, any>,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  if (!data) return prev

  const type = data.comments?.type as EventType
  const commentData: Comment = data.comments?.comment
  const cachedData = view(lens, prev) ?? []

  if (!commentData) return prev

  if (data.comments?.__typename === 'EntityDeletedEvent') {
    cachedData.filter((comment) => comment.id !== data.comments?.entityId)
    return
  }

  const eventTypeUpdaters = {
    [EventType.Created]: (cachedData: Comment[], commentData: Comment) => {
      const existingData = cachedData.find(
        (comment) => comment.id === commentData.id,
      )

      if (existingData) return cachedData

      return [commentData, ...cachedData]
    },
    [EventType.Updated]: (cachedData: Comment[], commentData: Comment) => {
      return cachedData.map((comment) =>
        comment.id === commentData.id ? commentData : comment,
      )
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  const newData = eventTypeUpdater(cachedData, commentData)

  return set(lens, newData, prev)
}

export const projectSizeMatrixSubscriptionDataHandler = (
  prev: Record<string, any>,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  if (!data) return prev

  const type = data.projectSizeMatrix?.type as EventType
  const sizeMatrixData: SizeMatrix = data.projectSizeMatrix?.sizeMatrix
  const cachedData = prev.searchSizeMatrices?.sizeMatrices

  const lens = lensPath(['searchSizeMatrices', 'sizeMatrices'])
  if (data.projectSizeMatrix?.__typename === 'EntityDeletedEvent') {
    const newData = cachedData.filter(
      (sizeMatrix) => sizeMatrix.id !== data.projectSizeMatrix?.entityId,
    )
    return set(lens, newData, prev)
  }

  if (!sizeMatrixData) return prev

  const eventTypeUpdaters = {
    [EventType.Created]: (
      cachedData: SizeMatrix[],
      sizeMatrixData: SizeMatrix,
    ) => {
      const existingData = cachedData.find(
        (sizeMatrix) => sizeMatrix.id === sizeMatrixData.id,
      )

      if (existingData) return cachedData

      return [sizeMatrixData, ...cachedData]
    },
    [EventType.Updated]: (
      cachedData: SizeMatrix[],
      sizeMatrixData: SizeMatrix,
    ) => {
      return cachedData.map((sizeMatrix) =>
        sizeMatrix.id === sizeMatrixData.id ? sizeMatrixData : sizeMatrix,
      )
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  const newData = eventTypeUpdater(cachedData, sizeMatrixData)

  return set(lens, newData, prev)
}

const taskCountersSubscriptionDataHandler = (
  prop: string,
  prev,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  if (typeof data?.counter?.value !== 'number') {
    return prev
  }

  return {
    ...prev,
    getTaskDetailsCounters: {
      ...prev.getTaskDetailsCounters,
      [prop]: data.counter.value,
    },
  }
}

const projectCountersSubscriptionDataHandler = (
  prop: string,
  prev,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  if (typeof data?.counter?.value !== 'number') {
    return prev
  }

  return {
    ...prev,
    getProjectDetailsCounters: {
      ...prev.getProjectDetailsCounters,
      [prop]: data.counter.value,
    },
  }
}

const navigationCountersSubscriptionDataHandler = (
  prop: string,
  prev,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  if (typeof data?.counter?.value !== 'number') {
    return prev
  }

  return {
    ...prev,
    getCounters: {
      ...prev.getCounters,
      [prop]: data.counter.value,
    },
  }
}

const threadCountersSubscriptionDataHandler = (
  prop: string,
  prev,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  if (typeof data?.counter?.value !== 'number') {
    return prev
  }

  return {
    ...prev,
    threadCounters: {
      ...prev.threadCounters,
      [prop]: data.counter.value,
    },
  }
}

export const userTaskCounterSubscriptionDataHandler = (
  prev,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  return {
    ...prev,
    getCounters: {
      ...prev.getCounters,
      tasks: {
        ...(prev.getCounters?.tasks ?? {}),
        ...data?.userTaskCounter,
      },
    },
  }
}

export const userToDoCounterSubscriptionDataHandler = (
  prev,
  { subscriptionData },
) => {
  const data = subscriptionData?.data

  return {
    ...prev,
    getCounters: {
      ...prev.getCounters,
      todos: {
        ...(prev.getCounters?.todos ?? {}),
        ...data?.userTodoCounter,
      },
    },
  }
}

export const remindersCountSubscriptionDataHandler = (prev, data) =>
  navigationCountersSubscriptionDataHandler('dynamicPlannings', prev, data)

export const todosCountSubscriptionDataHandler = (prev, data) =>
  navigationCountersSubscriptionDataHandler('todos', prev, data)

export const unreadThreadsCountSubscriptionDataHandler = (prev, data) =>
  threadCountersSubscriptionDataHandler('unread', prev, data)

export const taskCommentsCountSubscriptionDataHandler = (prev, data) =>
  taskCountersSubscriptionDataHandler('commentsCount', prev, data)

export const taskResourcesCountSubscriptionDataHandler = (prev, data) =>
  taskCountersSubscriptionDataHandler('resourcesCount', prev, data)

export const projectMilestonesCountSubscriptionDataHandler = (prev, data) =>
  projectCountersSubscriptionDataHandler('milestonesCount', prev, data)

export const projectResourcesCountSubscriptionDataHandler = (prev, data) =>
  projectCountersSubscriptionDataHandler('resourcesCount', prev, data)

export const projectSizeMatricesCountSubscriptionDataHandler = (prev, data) =>
  projectCountersSubscriptionDataHandler('sizeMatricesCount', prev, data)

export const goalCommentsSubscriptionDataHandler = (prev, data) =>
  commentsSubscriptionDataHandler(lensPath(['getGoal', 'comments']), prev, data)

export const taskCommentsSubscriptionDataHandler = (prev, data) =>
  commentsSubscriptionDataHandler(lensPath(['getTask', 'comments']), prev, data)

export const threadCommentsSubscriptionDataHandler = (
  client,
  data,
  threadId,
) => {
  const incoming = data?.data?.comments
  if (!incoming) return

  const cachedThreadId = client.cache.identify({
    __typename: 'Thread',
    id: threadId,
  })
  if (incoming.__typename === 'EntityDeletedEvent') {
    const entityId = incoming.entityId
    client.cache.modify({
      id: cachedThreadId,
      fields: {
        comments(existingRefs = [], { readField }) {
          return existingRefs.filter((ref) => readField('id', ref) !== entityId)
        },
      },
    })
  }

  const commentEvent = incoming as CommentEvent

  if (commentEvent.type === EventType.Created) {
    const comment = commentEvent.comment
    const newCommentRef = client.cache.writeFragment({
      data: comment,
      fragment: COMMENT_FRAGMENT,
      fragmentName: 'CommentFragment',
    })

    client.cache.modify({
      id: cachedThreadId,
      fields: {
        comments(existingRefs = [], { readField }) {
          const alreadyExists = existingRefs.some(
            (ref) => readField('id', ref) === comment.id,
          )
          if (alreadyExists) return existingRefs
          return [...existingRefs, newCommentRef]
        },
      },
    })
  }

  if (commentEvent.type === EventType.Updated) {
    const comment = commentEvent.comment
    client.cache.writeFragment({
      id: client.cache.identify(comment),
      fragment: COMMENT_FRAGMENT,
      fragmentName: 'CommentFragment',
      data: comment,
    })
  }
}

export const threadReadCommentsSubscriptionDataHandler = (
  client,
  data,
  threadId,
) => {
  const incoming = data?.data?.commentsRead
  if (!incoming) return

  const commentsReadEvent = incoming as CommentsReadEvent
  const accountId = commentsReadEvent.account?.id
  if (!accountId) return

  const cachedThreadId = client.cache.identify({
    __typename: 'Thread',
    id: threadId,
  })

  client.cache.modify({
    id: cachedThreadId,
    fields: {
      comments(existingRefs = [], { readField }) {
        return existingRefs.map((ref) => {
          const commentId = readField('id', ref)
          if (!commentId) return ref

          const fullComment = client.cache.readFragment({
            id: client.cache.identify({ __typename: 'Comment', id: commentId }),
            fragment: COMMENT_FRAGMENT,
            fragmentName: 'CommentFragment',
          })

          if (!fullComment?.recipients?.users?.length) return ref

          const updatedRecipients = {
            ...fullComment.recipients,
            users: fullComment.recipients.users.map((u) => {
              const userId = u.user?.id
              if (userId === accountId) {
                return { ...u, isRead: true }
              }
              return u
            }),
          }

          client.cache.writeFragment({
            id: client.cache.identify({ __typename: 'Comment', id: commentId }),
            fragment: COMMENT_FRAGMENT,
            fragmentName: 'CommentFragment',
            data: {
              ...fullComment,
              recipients: updatedRecipients,
            },
          })

          return ref
        })
      },
    },
  })
}

export const assignedGoalsSubscriptionDataHandler = (prev, data) =>
  myGoalsSubscriptionDataHandler(
    'assignedGoals',
    lensPath(['getAssignedGoals']),
    prev,
    data,
  )

export const milestoneTasksSubscriptionDataHandler = (prev, data) =>
  tasksSubscriptionDataHandler(
    lensPath(['getMilestone', 'tasks']),
    prev,
    data,
    'bottom',
    false,
  )

export const goalTasksSubscriptionDataHandler = (prev, data) =>
  tasksSubscriptionDataHandler(
    lensPath(['getGoal', 'tasks']),
    prev,
    data,
    'top',
    true,
  )

const myTasksGridSubscriptionDataHandler: ApolloParams['onSubscriptionData'] =
  ({ result, gridApi }) => {
    if (!gridApi || !result?.data) return

    const data = result.data.myTasks as
      | {
          __typename: 'TaskEvent'
          type: EventType
          task: Task
        }
      | {
          __typename: 'EntityDeletedEvent'
          entityId: UUID
        }

    if (!data) return

    if (data.__typename === 'EntityDeletedEvent') {
      gridApi.applyServerSideTransaction({
        remove: [{ id: data.entityId }],
      })
      return
    }

    const type = data.type
    const taskData = data.task

    if (!taskData) return

    const assignees = (taskData.assignees?.map((assignee) => {
      return cache.readFragment({
        id: 'Account:' + assignee.id,
        fragment: ACCOUNT_FULL_FRAGMENT,
        fragmentName: 'AccountFullFragment',
      })
    }) || []) as Account[]

    const enrichedTaskData = {
      ...taskData,
      assignees,
    }

    const eventTypeHandlers = {
      [EventType.Created]: (taskData: Task) => {
        gridApi.applyServerSideTransaction({
          add: [taskData],
        })
      },
      [EventType.Updated]: (taskData: Task) => {
        gridApi.applyServerSideTransaction({
          update: [taskData],
        })
      },
    }

    const eventTypeHandler = eventTypeHandlers[type]
    if (eventTypeHandler) {
      eventTypeHandler(enrichedTaskData)
    }
  }

export const projectsSubscriptionDataHandler: ApolloParams['onSubscriptionData'] =
  debounce(DEBOUNCE_TIME_MS, refreshGridOnSubscriptionData)

export const myTasksSubscriptionDataHandler: ApolloParams['onSubscriptionData'] =
  debounce(DEBOUNCE_TIME_MS, myTasksGridSubscriptionDataHandler)

export const recurringTaskListSubscriptionDataHandler: ApolloParams['onSubscriptionData'] =
  debounce(DEBOUNCE_TIME_MS, refreshGridOnSubscriptionData)

export const sizeMatricesListSubscriptionDataHandler: ApolloParams['onSubscriptionData'] =
  debounce(DEBOUNCE_TIME_MS, ({ gridApi }) => {
    gridApi?.refreshServerSide()
  })

export const notificationListSubscriptionDataHandler: ApolloParams['onSubscriptionData'] =
  debounce(DEBOUNCE_TIME_MS, ({ gridApi }) => {
    gridApi?.refreshServerSide()
  })

export const collectionsSubscriptionDataHandler: SubscribeToMoreUpdateQueryFn<
  any,
  OperationVariables,
  any
> = (prev, { subscriptionData }) => {
  const data = subscriptionData?.data

  if (!data) return prev

  const fashionCollectionSubsriptionData = data.fashionCollections
  const type = fashionCollectionSubsriptionData?.type as EventType
  const collectionData: FashionCollection =
    fashionCollectionSubsriptionData.collection
  const cachedData = prev.searchFashionCollections?.collections ?? []

  if (fashionCollectionSubsriptionData?.__typename === 'EntityDeletedEvent') {
    return {
      ...prev,
      searchFashionCollections: {
        ...prev.searchFashionCollections,
        collections: cachedData.filter(
          (c) => c.id !== fashionCollectionSubsriptionData?.entityId,
        ),
      },
    }
  }

  if (!collectionData) return prev

  const eventTypeUpdaters = {
    [EventType.Created]: (
      cachedData: FashionCollection[],
      collection: FashionCollection,
    ) => {
      const existingData = cachedData.find((c) => c.id === collection.id)

      if (existingData) return cachedData

      return [collectionData, ...cachedData]
    },
    [EventType.Updated]: (
      cachedData: FashionCollection[],
      collectionData: FashionCollection,
    ) => {
      return cachedData.map((c) =>
        c.id === collectionData.id ? collectionData : c,
      )
    },
  }

  const eventTypeUpdater = eventTypeUpdaters[type]

  if (!eventTypeUpdater) return prev

  return {
    ...prev,
    searchFashionCollections: {
      ...prev.searchFashionCollections,
      collections: eventTypeUpdater(cachedData, collectionData),
    },
  }
}

export const threadRecentCommentActivityDataHandler = async ({
  data,
  client,
  getThread,
  queryVariables,
}: {
  data: SubscriptionResult<ThreadCommentsSubscription, any>
  client: ApolloClient<object>
  getThread: LazyQueryExecFunction<GetThreadQuery, GetThreadQueryVariables>
  queryVariables: Record<string, any>
}) => {
  const threadCommentEvent = path(['data', 'threadComments'])(data)

  const response = await getThread({
    fetchPolicy: 'network-only',
    variables: { threadId: threadCommentEvent.thread.id },
  })

  const thread = response?.data?.getThread

  if (!thread) return

  client.cache.updateQuery(
    {
      query: SEARCH_RECENT_ACTIVITY_THREADS,
      variables: queryVariables,
    },
    (queryData) => {
      if (!queryData) {
        return queryData
      }
      const activities = queryData?.recentThreads?.activities ?? []

      const existingActivity = activities.find(
        (activity) => activity?.['thread']?.id === thread.id,
      )

      const typeName:
        | 'ThreadCommentActivity'
        | 'ThreadGoalActivity'
        | 'ThreadTaskActivity' = 'ThreadCommentActivity'

      // Handle system message for created task/goal
      if (
        threadCommentEvent.createdTask?.name ||
        threadCommentEvent.createdGoal?.name
      ) {
        return handleSystemCreatedThreadMessage({
          threadCommentEvent,
          typeName,
          thread,
          existingActivity,
          queryData,
          activities,
        })
      }

      const currentDate = new Date()
      const currentTimestamp = {
        ...getTimeStamp(currentDate),
        nanos: getNanos(currentDate),
        __typename: 'Timestamp',
      } as Timestamp
      if (!existingActivity && thread?.unreadCommentCount > 0) {
        return {
          ...queryData,
          recentThreads: {
            ...queryData.recentThreads,
            activities: [
              {
                __typename: typeName,
                lastParticipant: threadCommentEvent?.author,
                recentParticipants: [threadCommentEvent?.author],
                thread: thread as Thread,
                time: currentTimestamp,
              } satisfies ThreadCommentActivity,
              ...activities,
            ],
          },
        }
      }

      if (existingActivity && thread?.unreadCommentCount > 0) {
        return {
          ...queryData,
          recentThreads: {
            ...queryData.recentThreads,
            activities: activities
              .map((activity) => {
                if (activity?.['thread']?.id === thread.id) {
                  return {
                    ...activity,
                    time: currentTimestamp,
                    lastParticipant: threadCommentEvent?.author,
                    recentParticipants: [
                      ...(activity?.['recentParticipants'] ?? []),
                      threadCommentEvent?.author,
                    ],
                    thread: thread as Thread,
                  }
                }

                return activity
              })
              .sort((a, b) => {
                if (a?.['thread']?.id === thread.id) return -1
                if (b?.['thread']?.id === thread.id) return 1

                return 0
              }),
          },
        }
      }

      return queryData
    },
  )
}

const handleSystemCreatedThreadMessage = ({
  threadCommentEvent,
  typeName,
  thread,
  existingActivity,
  queryData,
  activities,
}) => {
  if (threadCommentEvent.createdTask?.name) {
    typeName = 'ThreadTaskActivity' as ThreadTaskActivity['__typename']
  } else {
    typeName = 'ThreadGoalActivity' as ThreadGoalActivity['__typename']
  }

  const isTerminal = threadCommentEvent.isTerminal

  const newActivity =
    typeName === 'ThreadTaskActivity'
      ? ({
          __typename: typeName,
          isTerminal: isTerminal,
          convertedBy: threadCommentEvent?.author,
          thread: thread as Thread,
          createdAt: getTimeStamp(new Date()),
          task: {
            id: threadCommentEvent?.createdTask?.id,
            name: threadCommentEvent?.createdTask?.name,
          } as Task,
        } satisfies ThreadTaskActivity)
      : ({
          __typename: typeName,
          isTerminal: isTerminal,
          convertedBy: threadCommentEvent?.author,
          thread: thread as Thread,
          createdAt: getTimeStamp(new Date()),
          goal: {
            id: threadCommentEvent?.createdGoal?.id,
            name: threadCommentEvent?.createdGoal?.name,
          } as Goal,
        } satisfies ThreadGoalActivity)

  if (isTerminal && existingActivity) {
    return {
      ...queryData,
      recentThreads: {
        ...queryData.recentThreads,
        activities: activities
          .map((activity) => {
            if (activity?.['thread']?.id === thread.id) {
              return newActivity
            }
            return activity
          })
          .sort((a, b) => {
            if (a?.['thread']?.id === thread.id) return -1
            if (b?.['thread']?.id === thread.id) return 1

            return 0
          }),
      },
    }
  }

  return {
    ...queryData,
    recentThreads: {
      ...queryData.recentThreads,
      activities: [newActivity, ...activities],
    },
  }
}

export const threadRecentLikeActivityDataHandler = ({
  data,
  client,
  queryVariables,
}: {
  data: OnDataOptions<ThreadLikesSubscription>['data']
  client: OnDataOptions<ThreadLikesSubscription>['client']
  queryVariables: Record<string, any>
}) => {
  const threadLike = path(['data', 'threadLikes'])(data) as ThreadLikeEvent

  if (!threadLike) return

  const isUnLike = !threadLike.time

  client.cache.updateQuery(
    {
      query: SEARCH_RECENT_ACTIVITY_THREADS,
      variables: queryVariables,
    },
    (queryData) => {
      const activities = queryData?.recentThreads?.activities ?? []

      if (isUnLike) {
        return {
          ...queryData,
          recentThreads: {
            ...queryData.recentThreads,
            activities: activities.filter(
              (activity) => activity?.['comment']?.id === threadLike.comment.id,
            ),
          },
        }
      }

      const existingActivity = activities.find(
        (activity) => activity?.['comment']?.id === threadLike.comment.id,
      )

      if (existingActivity) {
        return {
          ...queryData,
          recentThreads: {
            ...queryData.recentThreads,
            activities: activities.map((activity) => {
              if (activity?.['comment']?.id === threadLike.comment.id) {
                return {
                  ...activity,
                  accounts: [
                    ...(activity as ThreadLikeActivity).accounts,
                    threadLike.author,
                  ],
                  comment: {
                    ...threadLike.comment,
                  },
                  time: threadLike?.time,
                }
              }

              return activity
            }),
          },
        }
      }

      return {
        ...queryData,
        recentThreads: {
          ...queryData.recentThreads,
          activities: [
            {
              __typename:
                'ThreadLikeActivity' as ThreadLikeActivity['__typename'],
              accounts: [threadLike.author],
              comment: threadLike.comment,
              time: threadLike.time,
            },
            ...activities,
          ],
        },
      }
    },
  )
}
